import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Loader2, Building2 } from 'lucide-react';
import FeatureBadge from '@/components/FeatureBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const RoomRequestModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [minds, setMinds] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!API) throw new Error('Live requests are unavailable while the backend is offline.');
      const res = await fetch(`${API}/api/room-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_name: name.trim(),
          room_description: description.trim(),
          suggested_minds: minds.split(',').map(m => m.trim()).filter(Boolean),
        }),
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setName(''); setDescription(''); setMinds(''); setError(null); onClose(); }, 2000);
    } catch (e) {
      console.error('Room request failed:', e);
      setError('The live request desk is asleep. Please try again after the AI engine wakes.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(26,21,18,0.25)', backdropFilter: 'blur(16px)' }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-lg overflow-hidden"
          style={{ background: '#FDFCFB', border: '1px solid #E6E1DB', borderRadius: '16px', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.12)' }}
          initial={{ scale: 0.93, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid #E6E1DB' }}>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: '#B8860B' }} />
              <h2 className="font-display text-lg tracking-wide" style={{ color: '#1A1512' }}>Request a Custom Hall</h2>
              <FeatureBadge feature="customHallRequest" />
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:bg-[#F2EFE9]" style={{ color: '#8C8680' }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {submitted ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(46,125,50,0.1)' }}>
                <span className="text-lg" style={{ color: '#2E7D32' }}>&#10003;</span>
              </div>
              <h3 className="font-display text-lg mb-1" style={{ color: '#1A1512' }}>Request Submitted</h3>
              <p className="font-body text-sm" style={{ color: '#8C8680' }}>Your idea is saved to the build queue for future halls.</p>
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <p className="font-body text-sm" style={{ color: '#8C8680' }}>
                Propose a new hall for the museum. This is a build queue, not instant hall creation yet.
              </p>

              <div>
                <label className="font-ui text-[10px] tracking-wider uppercase mb-1.5 block" style={{ color: '#8C8680' }}>Hall Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Music Legends, Ancient Strategists..."
                  className="w-full px-4 py-3 rounded-lg text-sm font-body outline-none transition-all"
                  style={{ background: '#F2EFE9', border: '1px solid #E6E1DB', color: '#1A1512' }}
                  onFocus={e => e.target.style.borderColor = '#B8860B'} onBlur={e => e.target.style.borderColor = '#E6E1DB'}
                />
              </div>

              <div>
                <label className="font-ui text-[10px] tracking-wider uppercase mb-1.5 block" style={{ color: '#8C8680' }}>Description (optional)</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What should this hall be about?" rows={2}
                  className="w-full px-4 py-3 rounded-lg text-sm font-body outline-none transition-all resize-none"
                  style={{ background: '#F2EFE9', border: '1px solid #E6E1DB', color: '#1A1512' }}
                  onFocus={e => e.target.style.borderColor = '#B8860B'} onBlur={e => e.target.style.borderColor = '#E6E1DB'}
                />
              </div>

              <div>
                <label className="font-ui text-[10px] tracking-wider uppercase mb-1.5 block" style={{ color: '#8C8680' }}>Suggested Minds (comma-separated)</label>
                <input value={minds} onChange={e => setMinds(e.target.value)} placeholder="e.g. Mozart, Beethoven, Bach..."
                  className="w-full px-4 py-3 rounded-lg text-sm font-body outline-none transition-all"
                  style={{ background: '#F2EFE9', border: '1px solid #E6E1DB', color: '#1A1512' }}
                  onFocus={e => e.target.style.borderColor = '#B8860B'} onBlur={e => e.target.style.borderColor = '#E6E1DB'}
                />
              </div>

              <button onClick={handleSubmit} disabled={!name.trim() || submitting}
                className="w-full py-3.5 font-display text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2 transition-all cursor-pointer"
                style={{ background: '#1A1512', color: '#FDFCFB', opacity: name.trim() && !submitting ? 1 : 0.35, boxShadow: '0 4px 12px rgba(26,21,18,0.12)' }}>
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Plus className="w-4 h-4" /> Submit Request</>}
              </button>
              {error && <p className="font-body text-xs" style={{ color: '#C0392B' }}>{error}</p>}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RoomRequestModal;
